import { type PerformanceIndex, PerformanceMeasureType } from '@tencent/speed-insights';
import { DsiBenchmarkIndex } from './dsi-benchmark-index';
import { EXTREME_PERCENT, MAX_BENCHMARK_PERCENT, STD_BENCHMARK_PERCENT } from './dsi-consts';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { IDsiDelegateServices, ICcnrObserver } from '@src/slide/application/service/dsi-report-service/interface';
import { CcnrObserver } from '@src/slide/application/service/dsi-report-service/base/appoint/ccnr';

export class BaseDsiBenchmarkIndexes {
    public readonly indexes: PerformanceIndex[] = [];
    protected servicesPromise: Promise<IDsiDelegateServices>;
    private servicesResolve: (param: IDsiDelegateServices) => void;
    private ccnrObserver: ICcnrObserver;

    public constructor() {
        this.servicesPromise = new Promise(resolve => this.servicesResolve = resolve);
        this.ccnrObserver = new CcnrObserver();
        this.indexes = [
            {
                name: DsiBenchmarkIndex.FPR,
                unit: 'ms',
                measureOnce: () => this.measureFPR(),
                positive: false,
                thresholds: [2500, 4000],
                benchmarkPercent: STD_BENCHMARK_PERCENT,
                extremePercent: EXTREME_PERCENT,
                supportTypes: [
                    PerformanceMeasureType.Benchmark,
                ],
            },
            {
                name: DsiBenchmarkIndex.FPE,
                unit: 'ms',
                measureOnce: () => this.measureFPE(),
                positive: false,
                thresholds: [2600, 4300],
                benchmarkPercent: STD_BENCHMARK_PERCENT,
                extremePercent: EXTREME_PERCENT,
                supportTypes: [
                    PerformanceMeasureType.Benchmark,
                ],
            },
            {
                name: DsiBenchmarkIndex.CCNR,
                unit: 'ms',
                measureOnce: () => this.measureCCNR(),
                positive: false,
                thresholds: [200, 500],
                benchmarkPercent: MAX_BENCHMARK_PERCENT,
                extremePercent: EXTREME_PERCENT,
                supportTypes: [
                    PerformanceMeasureType.Benchmark,
                    PerformanceMeasureType.Extreme,
                    PerformanceMeasureType.Stability,
                ],
            },
        ];
    }

    public setServiceDelegate(service: IDsiDelegateServices): void {
        this.servicesResolve(service);
    }

    /**
     * 获取CCNR 切页耗时
     * @private
     */
    private async measureCCNR(): Promise<number> {
        const service = await this.servicesPromise;
        const { currentSlide } = service.selectionService;
        const currentSlideIndex = currentSlide?.parent?.children.findIndex(e => e === currentSlide);
        if (!currentSlideIndex && (currentSlideIndex !== 0)) {
            return -1;
        }
        const promise = this.ccnrObserver.getSlideChangePromise(currentSlideIndex);
        return promise || -1;
    }

    /**
     * 获取FPR 首屏可见
     * @private
     */
    private async measureFPR(): Promise<number> {
        const service = await this.servicesPromise;
        const { time: tti } = await service.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.firstSlideRender].getPointCompletePromise();
        const pageStartTime = global.performance.timing.fetchStart;
        // 有ssr用ssr，没有则用tti
        return global.previewTime ? (global.previewTime - pageStartTime) : tti;
    }

    /**
     * 获取FPE 首屏可编辑
     * @private
     */
    private async measureFPE(): Promise<number> {
        const service = await this.servicesPromise;
        const { time: tti } = await service.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.firstSlideRender].getPointCompletePromise();
        return tti;
    }
}
